import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@mui/material';
import { useFetchPublicWantListQuery } from 'store/services/collection/publicCollectionApi';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import {
  useAddCardsToTradeMutation,
  SetDetail,
} from 'store/services/trade/tradeApi';
import { Trade } from '../../ActiveTradesPage';
import { useGetPublicProfileQuery } from 'store/services/profile/profileApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { SortOrder } from 'types/interfaces/sort';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import { FoilCardImage } from 'components/FoilCardImage';
import TextInputField from 'components/TextInputField';
import CardsTable from 'components/CardsTable';
import { CardsTableCellsData } from 'components/CardsTable/types';
import { SetIcon } from 'components/SetIcon';
import { AssetUrls } from 'config/assets';
import { getFinishAvailability } from 'utils/foilAvailability';
import {
  getCardImageUrl,
  getValidCondition,
  shouldShowFoil,
  SORT_OPTIONS,
  VIEW_OPTIONS,
  formatConditions,
  formatFoilPreference,
} from 'utils/wantListHelpers';
import { formatLanguages } from 'constants/languages';
import StagedCardItem from './StagedCardItem';
import './AddCardsToTradeModal.css';

interface AddCardsToTradeModalProps {
  trade: Trade;
  isOpen: boolean;
  onClose: () => void;
}

// Chevron SVG for dropdowns
const ChevronSvg: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`add-cards-dropdown-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const AddCardsToTradeModal: React.FC<AddCardsToTradeModalProps> = ({ trade, isOpen, onClose }) => {
  const navigate = useNavigate();

  // Staged cards state
  const [stagedCards, setStagedCards] = useState<SidebarTradeCard[]>([]);

  // Browsing state
  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<CollectionList[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<{ sort: string; direction: SortOrder }>({
    sort: 'created_at',
    direction: 'desc',
  });
  const [viewMode, setViewMode] = useState<'images' | 'list'>('images');

  // Toggle states
  const [autoMatchEnabled, setAutoMatchEnabled] = useState(false);
  const [showAffordableOnly, setShowAffordableOnly] = useState(false);

  // Dropdown states
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);

  // Price calculation
  const [priceMap, setPriceMap] = useState<Record<number, number>>({});
  const [calculatePrice] = useCalculatePriceMutation();
  const fetchedIdsRef = useRef<Set<number>>(new Set());

  // API hooks
  const [addCardsToTrade] = useAddCardsToTradeMutation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [calculatingIds, setCalculatingIds] = useState<Set<string>>(new Set());

  const handleCalculatingChange = useCallback((id: string, isCalc: boolean) => {
    setCalculatingIds(prev => {
      const next = new Set(prev);
      isCalc ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const anyCalculating = calculatingIds.size > 0;

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Receiver balance
  const { data: receiverProfile } = useGetPublicProfileQuery(trade.traderName, {
    skip: !isOpen,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset pagination on sort/search change
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
    setPriceMap({});
    fetchedIdsRef.current.clear();
  }, [debouncedSearchTerm, sortOrder.sort, sortOrder.direction, autoMatchEnabled, showAffordableOnly]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) setIsSortOpen(false);
      if (viewRef.current && !viewRef.current.contains(event.target as Node)) setIsViewOpen(false);
    };
    if (isSortOpen || isViewOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen, isViewOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Affordable Only: filter by receiver's balance
  const affordableMaxPrice = showAffordableOnly && receiverProfile?.points != null
    ? receiverProfile.points
    : undefined;

  // Fetch want list
  const { data, isLoading, isFetching } = useFetchPublicWantListQuery({
    username: trade.traderName,
    page,
    limit: 48,
    sort: sortOrder.sort,
    direction: sortOrder.direction,
    search: debouncedSearchTerm || undefined,
    autoMatch: autoMatchEnabled || undefined,
    maxPrice: affordableMaxPrice,
  }, { skip: !isOpen });

  // Fetch prices for items without displayPrice
  useEffect(() => {
    if (!data?.wantList || data.wantList.length === 0) return;
    const itemsNeedingPrice = data.wantList.filter(
      item => item.displayPrice == null && !fetchedIdsRef.current.has(item.id)
    );
    if (itemsNeedingPrice.length === 0) return;
    itemsNeedingPrice.forEach(item => fetchedIdsRef.current.add(item.id));

    const fetchPrices = async () => {
      try {
        const result = await calculatePrice({
          cards: itemsNeedingPrice.map(item => {
            // Resolve set details to determine physical foil availability
            let setDetails = item.acceptableSetDetails;
            if (!setDetails || setDetails.length === 0) {
              setDetails = item.acceptableSets?.map((setName, idx) => ({
                name: setName,
                code: item.acceptableSetCodes?.[idx] || setName.toLowerCase().replace(/\s+/g, ''),
                cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
              }));
            }
            const matchingSetDetail = setDetails?.find(
              s => s.cardId === item.card?.id
            ) || setDetails?.[0];

            const acceptFoil = item.acceptFoil ?? true;
            const acceptNonFoil = item.acceptNonFoil ?? true;
            const { defaultFoil } = getFinishAvailability(
              matchingSetDetail, item.card, acceptFoil, acceptNonFoil
            );

            return {
              wantListId: item.id,
              cardId: item.card?.id?.toString(),
              condition: getValidCondition(item.acceptableConditions?.[0]),
              language: 'EN',
              foil: defaultFoil,
              set: item.card?.setName,
            };
          })
        }).unwrap();
        const newPrices: Record<number, number> = {};
        result.cards.forEach(card => { newPrices[card.wantListId] = card.displayPrice; });
        setPriceMap(prev => ({ ...prev, ...newPrices }));
      } catch (error) {
        console.error('Failed to calculate prices:', error);
      }
    };
    fetchPrices();
  }, [data?.wantList, calculatePrice]);

  // Combine pages
  const allItems = useMemo(
    () => [...previousPages, ...(data?.wantList || [])],
    [previousPages, data?.wantList]
  );

  const handleLoadMore = () => {
    if (data?.wantList) {
      setPreviousPages(prev => [...prev, ...data.wantList]);
    }
    setPage(p => p + 1);
  };

  // Count copies of a want-list row currently staged in this session.
  // The public want-list endpoint already returns the uncommitted remainder
  // (backend decrements on trade initiation), so we only count staged here.
  const getStagedCount = useCallback((wantListId: number) => {
    return stagedCards.filter(c => c.wantListId === wantListId).length;
  }, [stagedCards]);

  // Add card to staged
  const handleAddToPackage = useCallback((item: CollectionList) => {
    const cardName = item.card?.name || item.cardName || 'Unknown Card';
    const imageUrl = getCardImageUrl(item);
    const defaultCondition = getValidCondition(item.acceptableConditions?.[0]);

    // Resolve set details FIRST so we can derive foil availability from them
    let acceptableSetDetails: SetDetail[] | undefined = item.acceptableSetDetails;
    if (!acceptableSetDetails || acceptableSetDetails.length === 0) {
      acceptableSetDetails = item.acceptableSets?.map((setName, idx) => ({
        name: setName,
        code: item.acceptableSetCodes?.[idx] || setName.toLowerCase().replace(/\s+/g, ''),
        cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
      }));
    }

    // Find the set detail matching item.card so dropdown aligns with displayed image/price
    const matchingSetDetail = acceptableSetDetails?.find(
      s => s.cardId === item.card?.id
    ) || acceptableSetDetails?.[0];

    // Determine foil availability and default using SetDetail → price data → card.foil fallback
    const acceptFoil = item.acceptFoil ?? true;
    const acceptNonFoil = item.acceptNonFoil ?? true;
    const { availableInFoil, availableInNonFoil, defaultFoil } = getFinishAvailability(
      matchingSetDetail, item.card, acceptFoil, acceptNonFoil,
    );

    const id = `staged-${item.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newCard: SidebarTradeCard = {
      id,
      wantListId: item.id,
      cardId: matchingSetDetail?.cardId || item.card?.id,
      cardName,
      set: matchingSetDetail?.name || item.card?.setName || 'Unknown Set',
      setCode: matchingSetDetail?.code || item.card?.setCode || '',
      condition: defaultCondition,
      language: 'EN',
      foil: defaultFoil,
      imageUrl,
      pointValue: item.displayPrice ?? priceMap[item.id] ?? 0,
      acceptableSetDetails,
      acceptableConditions: item.acceptableConditions,
      acceptFoil,
      acceptNonFoil,
      availableInFoil,
      availableInNonFoil,
      maxQuantity: item.quantity,
    };

    setStagedCards(prev => [...prev, newCard]);
    if (isMobile) setIsMobileSidebarOpen(true);
  }, [priceMap, isMobile]);

  const handleRemoveStagedCard = useCallback((id: string) => {
    setStagedCards(prev => prev.filter(c => c.id !== id));
  }, []);

  const handleUpdateStagedCard = useCallback((id: string, updates: Partial<SidebarTradeCard>) => {
    setStagedCards(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  }, []);

  // Totals
  const stagedTotalPoints = useMemo(
    () => stagedCards.reduce((sum, card) => sum + card.pointValue, 0),
    [stagedCards]
  );

  const receiverBalance = receiverProfile?.points ?? 0;
  const receiverRemainingBalance = receiverBalance - stagedTotalPoints;
  const isOverBudget = stagedTotalPoints > 0 && receiverRemainingBalance < 0;

  // Sort handlers
  const currentSortValue = `${sortOrder.sort}-${sortOrder.direction}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Date Added';

  const handleSortSelect = useCallback((compoundValue: string) => {
    const [sort, direction] = compoundValue.split('-');
    setSortOrder({ sort, direction: direction as SortOrder });
    setIsSortOpen(false);
  }, []);

  const handleViewModeChange = useCallback((mode: 'images' | 'list') => {
    setViewMode(mode);
    setIsViewOpen(false);
  }, []);

  // Confirm handler
  const handleConfirm = async () => {
    if (stagedCards.length === 0) return;
    setIsConfirming(true);
    try {
      const result = await addCardsToTrade({
        tradeId: trade.id,
        cards: stagedCards.map(card => ({
          wantListId: card.wantListId,
          cardId: card.cardId?.toString(),
          quantity: 1,
          condition: card.condition,
          language: card.language,
          foil: card.foil,
          set: card.set,
        })),
      }).unwrap();

      if (result.success) {
        toast.success(result.message || `${result.addedCards ?? stagedCards.length} card(s) added to package!`);
        onClose();
      } else {
        toast.error(result.message || 'Failed to add cards.');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to add cards. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  const hasMore = data?.hasMore || false;
  const isSearchPending = searchTerm !== debouncedSearchTerm;
  const isEmpty = allItems.length === 0;
  const showSearchEmptyState = isEmpty && debouncedSearchTerm && !isSearchPending && !isFetching;
  const showGenericEmptyState = isEmpty && !debouncedSearchTerm && !isSearchPending && !isFetching;
  const showLoadingState = isEmpty && (isSearchPending || isFetching);

  // List view table data
  const tableCellsData: CardsTableCellsData = useMemo(() => {
    if (viewMode !== 'list') return { headCells: [], bodyRows: [] };

    const headCells: { key: string; label: string; columnWidth?: number; noSort?: boolean }[] = [
      { key: 'card_name', label: 'Name' },
      { key: 'setName', label: 'Set', columnWidth: 65, noSort: true },
    ];

    if (!isMobile) {
      headCells.push(
        { key: 'condition', label: 'Cond', columnWidth: 40, noSort: true },
        { key: 'lang', label: 'Lang', columnWidth: 35, noSort: true },
        { key: 'isFoil', label: 'Foil', columnWidth: 35, noSort: true },
        { key: 'reference_price', label: 'Price', columnWidth: 70, noSort: true },
      );
    }

    headCells.push({ key: 'quantity', label: 'Qty', columnWidth: 35, noSort: true });
    headCells.push({ key: 'action', label: '', columnWidth: 115, noSort: true });

    return {
      headCells,
      bodyRows: allItems.map((row) => {
        const stagedCount = getStagedCount(row.id);
        const remaining = row.quantity - stagedCount;
        const allAdded = remaining <= 0;

        const components: React.ReactNode[] = [
          // Name with thumbnail
          <div className="collection-card-name" key="name">
            {row.card?.scryfallID && (
              <img
                src={AssetUrls.cards.getCardImage(row.card.scryfallID, 'art_crop')}
                className="collection-card-name-img"
                alt={row.cardName || row.card?.name || 'Card'}
                onError={(e) => {
                  const img = e.currentTarget;
                  img.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
            <span className="collection-card-name-text">
              {row.cardName || row.card?.name}
            </span>
          </div>,
          // Set
          <div className="collection-card-set" key="set">
            {(!row.acceptableSets || row.acceptableSets.length === 0) ? (
              <span className="collection-card-set-text">Any</span>
            ) : (
              <>
                <SetIcon
                  setIconUrl={
                    row.card?.setIconUrl ||
                    (row.acceptableSetCodes?.[0]
                      ? `${AssetUrls.cardsCDN}/sets/icons/${row.acceptableSetCodes[0]}.svg`
                      : undefined)
                  }
                  setName={row.acceptableSets[0]}
                  inverted={false}
                  size="sm"
                />
                <span className="collection-card-set-code">
                  {(row.acceptableSetCodes?.[0] || row.card?.setCode || '').toUpperCase()}
                </span>
                {row.acceptableSets.length > 1 && (
                  <span className="collection-card-set-more" title={row.acceptableSets.join(', ')}>
                    +{row.acceptableSets.length - 1}
                  </span>
                )}
              </>
            )}
          </div>,
        ];

        if (!isMobile) {
          components.push(
            <div key="cond">{formatConditions(row.acceptableConditions)}</div>,
            <div key="lang">{formatLanguages(row.acceptableLanguages)}</div>,
            <div key="foil">{formatFoilPreference(row.acceptFoil, row.acceptNonFoil)}</div>,
            <div key="price">
              {(row.displayPrice ?? priceMap[row.id]) != null
                ? <Points points={(row.displayPrice ?? priceMap[row.id])!} type="small" />
                : '???'}
            </div>,
          );
        }

        // Quantity
        components.push(
          <div className="collection-card-quantity-container" key="qty">
            {allAdded ? (
              <span className="add-cards-list-qty-done">&#10003;</span>
            ) : (
              <span>{row.quantity}</span>
            )}
          </div>,
        );

        // Action
        components.push(
          <div key="action">
            {allAdded ? (
              <span className="add-cards-list-added">Added</span>
            ) : (
              <button
                className="add-cards-list-add-btn"
                onClick={(e) => { e.stopPropagation(); handleAddToPackage(row); }}
              >
                Add to Package
              </button>
            )}
          </div>
        );

        return {
          key: row.id.toString(),
          card: row.card || ({} as any),
          components,
          onClick: () => {
            if (row.card) {
              navigate(buildCardUrl(row.card));
            }
          },
        };
      }),
    };
  }, [viewMode, allItems, isMobile, priceMap, getStagedCount, handleAddToPackage, navigate]);

  // Grid card item renderer
  const renderGridCard = (item: CollectionList) => {
    const cardName = item.card?.name || item.cardName || 'Unknown Card';
    const imageUrl = getCardImageUrl(item);
    const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;
    const stagedCount = getStagedCount(item.id);
    const remaining = item.quantity - stagedCount;
    const allAdded = remaining <= 0;

    return (
      <div key={item.id} className="add-cards-grid-card">
        <div className="add-cards-grid-card-image-container">
          <FoilCardImage
            src={imageUrl}
            alt={cardName}
            className="add-cards-grid-card-image"
            foil={shouldShowFoil(item)}
            onError={(e: any) => { e.currentTarget.src = defaultImage; }}
            loading="lazy"
            borderRadius="8px"
          />

          {/* Quantity badge */}
          {item.quantity > 1 && (
            <div className="add-cards-grid-card-quantity">x{item.quantity}</div>
          )}

          {/* Overlay */}
          <div className="add-cards-grid-card-overlay">
            <span className="add-cards-grid-card-overlay-title">{cardName}</span>
            {remaining > 1 && (
              <div className="add-cards-grid-card-overlay-remaining">{remaining} left</div>
            )}
            {allAdded ? (
              <div className="add-cards-grid-card-overlay-added">
                <span>&#10003;</span>
                <span>Added</span>
              </div>
            ) : (
              <button
                className="add-cards-grid-card-overlay-btn"
                onClick={(e) => { e.stopPropagation(); handleAddToPackage(item); }}
              >
                <span>Add to Package</span>
                <img src={AssetUrls.icons.navigation.sending} alt="" className="add-cards-grid-card-overlay-btn-icon" />
              </button>
            )}
          </div>
        </div>
        <div className="add-cards-grid-card-info">
          {(item.displayPrice ?? priceMap[item.id]) != null && (
            <Points points={(item.displayPrice ?? priceMap[item.id])!} type="small" />
          )}
        </div>
      </div>
    );
  };

  // Sidebar content
  const renderSidebar = () => (
    <div className={`add-cards-sidebar ${isMobile ? 'mobile' : ''}`}>
      <div className="add-cards-sidebar-header">
        <span className="add-cards-sidebar-title">
          Cards to Add ({stagedCards.length})
        </span>
        {stagedCards.length > 0 && (
          <Points points={stagedTotalPoints} type="small" />
        )}
      </div>

      <div className="add-cards-sidebar-cards">
        {stagedCards.length === 0 ? (
          <div className="add-cards-sidebar-empty">
            <p>Browse the want list and click "Add to Package" to stage cards.</p>
          </div>
        ) : (
          stagedCards.map(card => (
            <StagedCardItem
              key={card.id}
              card={card}
              onUpdate={handleUpdateStagedCard}
              onRemove={handleRemoveStagedCard}
              onCalculatingChange={handleCalculatingChange}
            />
          ))
        )}
      </div>

      <div className="add-cards-sidebar-footer">
        <div className="add-cards-sidebar-summary">
          <div className="add-cards-sidebar-summary-row">
            <span>Current package:</span>
            <Points points={trade.totalValue} type="small" />
          </div>
          {stagedCards.length > 0 && (
            <>
              <div className="add-cards-sidebar-summary-row">
                <span>Adding:</span>
                <span className="add-cards-sidebar-adding">
                  +{stagedCards.length} {stagedCards.length === 1 ? 'card' : 'cards'} (<Points points={stagedTotalPoints} type="small" />)
                </span>
              </div>
              <div className="add-cards-sidebar-summary-row">
                <span>New total:</span>
                <Points points={trade.totalValue + stagedTotalPoints} type="small" />
              </div>
              <div className={`add-cards-sidebar-summary-row ${isOverBudget ? 'over-budget' : ''}`}>
                <span>Receiver balance after:</span>
                <span className={isOverBudget ? 'add-cards-sidebar-over-budget-value' : ''}>
                  {receiverRemainingBalance.toLocaleString()} pts
                </span>
              </div>
            </>
          )}
        </div>

        {isOverBudget && (
          <div className="add-cards-sidebar-warning">
            {trade.traderName} is {Math.abs(receiverRemainingBalance).toLocaleString()} points short
          </div>
        )}

        <div className="add-cards-sidebar-actions">
          <MagicButton
            label="Cancel"
            color="dark-grey"
            onPress={onClose}
            isDisabled={isConfirming}
          />
          <MagicButton
            label={isConfirming ? 'Adding...' : `Add ${stagedCards.length} Card${stagedCards.length !== 1 ? 's' : ''}`}
            color="purple"
            onPress={handleConfirm}
            isDisabled={stagedCards.length === 0 || isOverBudget || isConfirming || anyCalculating}
            isLoading={isConfirming}
          />
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  const modalContent = (
    <div className="add-cards-modal-overlay" onClick={onClose}>
      <div className="add-cards-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-cards-modal-header">
          <div className="add-cards-modal-header-left">
            <h2 className="add-cards-modal-title">
              Add Cards for {trade.traderName}
            </h2>
            <span className="add-cards-modal-subtitle">
              Trade #{trade.tradeNumber}
            </span>
          </div>
          <button className="add-cards-modal-close" onClick={onClose}>
            <img src={AssetUrls.icons.dropdowns.close} alt="Close" className="add-cards-modal-close-icon" />
          </button>
        </div>

        {/* Controls bar */}
        <div className="add-cards-controls">
          <div className="add-cards-search">
            <TextInputField
              leftComponent={<FontAwesomeIcon icon="magnifying-glass" color="#DD5BEA" size="sm" />}
              value={searchTerm}
              placeholder="Card Name"
              onChange={setSearchTerm}
            />
          </div>

          {/* Auto-Match Toggle */}
          <div className="add-cards-auto-match-toggle">
            <label className="add-cards-toggle-label">
              <span className="add-cards-auto-match-icon">&#9889;</span>
              <span className="add-cards-toggle-text">Auto-Match</span>
              <div className="add-cards-toggle-switch">
                <input
                  type="checkbox"
                  checked={autoMatchEnabled}
                  onChange={(e) => setAutoMatchEnabled(e.target.checked)}
                />
                <span className="add-cards-toggle-slider" />
              </div>
            </label>
          </div>

          {/* Affordable Only Toggle */}
          <div className="add-cards-affordable-toggle">
            <label className="add-cards-toggle-label">
              <span className="add-cards-toggle-text">Affordable Only</span>
              <div className="add-cards-toggle-switch">
                <input
                  type="checkbox"
                  checked={showAffordableOnly}
                  onChange={(e) => setShowAffordableOnly(e.target.checked)}
                />
                <span className="add-cards-toggle-slider" />
              </div>
            </label>
          </div>

          <div className="add-cards-view-selector" ref={viewRef}>
            <button
              type="button"
              className={`add-cards-dropdown-trigger ${isViewOpen ? 'open' : ''}`}
              onClick={() => setIsViewOpen(!isViewOpen)}
            >
              <img
                src={VIEW_OPTIONS.find(o => o.value === viewMode)?.icon}
                alt=""
                className="add-cards-dropdown-icon"
              />
              <span className="add-cards-dropdown-text">
                {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
              </span>
              <ChevronSvg open={isViewOpen} />
            </button>
            {isViewOpen && (
              <div className="add-cards-dropdown-menu" role="listbox">
                {VIEW_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-dropdown-option ${viewMode === option.value ? 'selected' : ''}`}
                    onClick={() => handleViewModeChange(option.value)}
                  >
                    <img src={option.icon} alt="" className="add-cards-dropdown-option-icon" />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="add-cards-sort-selector" ref={sortRef}>
            <button
              type="button"
              className={`add-cards-dropdown-trigger ${isSortOpen ? 'open' : ''}`}
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <img src={AssetUrls.icons.controls.sort} alt="" className="add-cards-dropdown-icon" />
              <span className="add-cards-dropdown-text">{currentSortLabel}</span>
              <ChevronSvg open={isSortOpen} />
            </button>
            {isSortOpen && (
              <div className="add-cards-dropdown-menu" role="listbox">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-dropdown-option ${currentSortValue === option.value ? 'selected' : ''}`}
                    onClick={() => handleSortSelect(option.value)}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main content area */}
        <div className="add-cards-content">
          {/* Want list browsing area */}
          <div className="add-cards-want-list">
            {isLoading && (
              <div className="add-cards-grid">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="add-cards-grid-card">
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      sx={{ backgroundColor: 'rgba(255,255,255,0.1)', aspectRatio: '488 / 680', borderRadius: '8px' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {showLoadingState && !isLoading && (
              <div className="add-cards-grid">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="add-cards-grid-card">
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      sx={{ backgroundColor: 'rgba(255,255,255,0.1)', aspectRatio: '488 / 680', borderRadius: '8px' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {showSearchEmptyState && (
              <div className="add-cards-empty">
                <p>No cards matching "{debouncedSearchTerm}"</p>
              </div>
            )}

            {showGenericEmptyState && (
              <div className="add-cards-empty">
                <p>{trade.traderName}'s want list is empty.</p>
              </div>
            )}

            {!isEmpty && viewMode === 'images' && (
              <div className="add-cards-grid">
                {allItems.map(renderGridCard)}
              </div>
            )}

            {!isEmpty && viewMode === 'list' && (
              <div className="add-cards-list-view">
                <CardsTable
                  isLoading={false}
                  cellsData={tableCellsData}
                  orderBy={sortOrder.sort}
                  order={sortOrder.direction}
                />
              </div>
            )}

            {hasMore && !isEmpty && (
              <div className="add-cards-load-more">
                <MagicButton
                  label={isFetching ? 'Loading...' : 'Load More'}
                  color="dark-grey"
                  onPress={handleLoadMore}
                  isLoading={isFetching}
                  isDisabled={isFetching}
                />
              </div>
            )}
          </div>

          {/* Sidebar (desktop) */}
          {!isMobile && renderSidebar()}
        </div>

        {/* Mobile bottom bar */}
        {isMobile && !isMobileSidebarOpen && stagedCards.length > 0 && (
          <div className="add-cards-mobile-bar" onClick={() => setIsMobileSidebarOpen(true)}>
            <div className="add-cards-mobile-bar-left">
              <img src={AssetUrls.icons.navigation.sending} alt="" className="add-cards-mobile-bar-icon" />
              <span>{stagedCards.length} {stagedCards.length === 1 ? 'card' : 'cards'}</span>
            </div>
            <div className="add-cards-mobile-bar-right">
              <Points points={stagedTotalPoints} type="small" />
              <img src={AssetUrls.icons.dropdowns.arrowUp} alt="" className="add-cards-mobile-bar-arrow" />
            </div>
          </div>
        )}

        {/* Mobile sidebar drawer */}
        {isMobile && isMobileSidebarOpen && (
          <>
            <div className="add-cards-mobile-overlay" onClick={() => setIsMobileSidebarOpen(false)} />
            <div className="add-cards-mobile-drawer">
              <div className="add-cards-mobile-drawer-handle" onClick={() => setIsMobileSidebarOpen(false)}>
                <div className="add-cards-mobile-drawer-handle-bar" />
              </div>
              {renderSidebar()}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddCardsToTradeModal;
